import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import { DialogTitle } from '@onesource/components';
import { RejectReviewSubmissionSchema, type TRejectReviewSubmission } from '@onesource/schemas';
import { useRejectReviewSubmission } from '@onesource/services';
import { enqueueSnackbar } from 'notistack';
import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdBlock, MdClose } from 'react-icons/md';

interface RejectSubmissionDialogProps {
  open: boolean;
  onClose: () => void;
  spaceId: string;
  submissionId: string;
  studentName: string;
  /** The teacher has unsent feedback — it is kept, but the student never sees it. */
  hasDraft: boolean;
}

/**
 * Reject a submission instead of reviewing it. The reason is required: it is
 * what the student reads, in-app and by email, before they submit again.
 */
export const RejectSubmissionDialog: FC<RejectSubmissionDialogProps> = ({
  open,
  onClose,
  spaceId,
  submissionId,
  studentName,
  hasDraft,
}) => {
  const reject = useRejectReviewSubmission();
  const { control, handleSubmit, reset } = useForm<TRejectReviewSubmission>({
    resolver: zodResolver(RejectReviewSubmissionSchema),
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (open) {
      reset({ reason: '' });
      reject.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = async ({ reason }: TRejectReviewSubmission) => {
    try {
      await reject.mutateAsync({ spaceId, submissionId, reason });
      enqueueSnackbar(`Submission rejected — ${studentName} was asked to submit again`, { variant: 'success' });
      onClose();
    } catch {
      // Shown in the dialog.
    }
  };

  return (
    <Dialog open={open} onClose={reject.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        onClose={onClose}
        icon={<MdBlock size={20} />}
        subtitle={`${studentName} is notified and can submit the form again.`}
      >
        Reject submission
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ pt: 2 }}>
          <Controller
            name="reason"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                autoFocus
                multiline
                minRows={4}
                fullWidth
                label="Reason"
                placeholder="For example: the video cuts off before the end of the movement. Please record the full exercise."
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
          {hasDraft && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Your unsent feedback stays with this submission. The student does not see it.
            </Alert>
          )}
          {reject.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {reject.error.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="soft" startIcon={<MdClose />} onClick={onClose} disabled={reject.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={reject.isPending}
            startIcon={reject.isPending ? <CircularProgress size={16} color="inherit" /> : <MdBlock />}
          >
            Reject submission
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
